/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * The v2 settings menu (design reference "Settings · Overview"): grouped
 * sections, each opening the page its card opens today, with the deeper pages
 * inside it listed under it. Pure, so it is tested on its own
 * (settingsNav.test.js).
 *
 * Which sections exist is not decided here: buildSettingsNav keeps only the
 * titles useSettingsSections returned, so admin-only and add-on-locked
 * sections behave in the menu exactly as their cards do. Deeper pages come
 * from SETTINGS_AREAS (the same list search uses), so the two cannot drift.
 */

import {
  AppWindow,
  Bot,
  Building2,
  CreditCard,
  FormInput,
  Globe,
  Inbox,
  KeyRound,
  LayoutGrid,
  LayoutTemplate,
  Layers,
  LifeBuoy,
  ListTree,
  Mail,
  PanelLeft,
  Phone,
  Plane,
  House,
  Puzzle,
  ShieldCheck,
  Sparkles,
  Users,
  UsersRound,
  WandSparkles,
  Award,
} from "lucide-react";
import { SETTINGS_AREAS, searchSettings } from "../settingsSearch";

export const OVERVIEW_TITLE = "Overview";

/** Menu groups in order, each listing section titles as Settings.js names them. */
export const SETTINGS_GROUPS = [
  { key: "general", label: "General", titles: [OVERVIEW_TITLE, "Company Settings", "User Management", "Roles & Permissions", "Teams", "Support Access"] },
  // Website sits with the channels: a travel or a real estate tenant can have one.
  { key: "channels", label: "Channels", titles: ["Telephony", "Email to Case", "Web Forms", "Website", "Integrations", "Connected apps"] },
  { key: "automation", label: "Automation and AI", titles: ["Automation Builder", "SEEP AI", "AI Assistant", "Queues", "Skills"] },
  { key: "data", label: "Data", titles: ["Custom Objects", "Page Layouts", "Sidebar Navigation"] },
  { key: "travel", label: "Travel", titles: ["Travel", "Payments"] },
  { key: "real_estate", label: "Real Estate", titles: ["Real Estate"] },
  { key: "developer", label: "Developer", titles: ["Developer & API"] },
];

export const SETTINGS_ICONS = {
  [OVERVIEW_TITLE]: LayoutGrid,
  "Company Settings": Building2,
  "User Management": Users,
  "Roles & Permissions": ShieldCheck,
  Teams: UsersRound,
  "Support Access": LifeBuoy,
  Telephony: Phone,
  "Email to Case": Mail,
  "Web Forms": FormInput,
  Integrations: Puzzle,
  "Connected apps": AppWindow,
  "Automation Builder": WandSparkles,
  "SEEP AI": Bot,
  "AI Assistant": Sparkles,
  Queues: Inbox,
  Skills: Award,
  "Custom Objects": Layers,
  "Page Layouts": LayoutTemplate,
  "Sidebar Navigation": PanelLeft,
  Travel: Plane,
  "Real Estate": House,
  Website: Globe,
  Payments: CreditCard,
  "Developer & API": KeyRound,
};

const FALLBACK_ICON = ListTree;
const OTHER_GROUP = { key: "other", label: "More" };

/** Where a section opens: its own page, or the Settings page with its ?tab. */
export function sectionTarget(section) {
  if (section.isNavigate && section.navigateTo) return section.navigateTo;
  if (section.urlTab) return `/settings?tab=${section.urlTab}`;
  return "/settings";
}

/** The deeper pages of a section that have an address, one per address. */
export function sectionChildren(title, areas = SETTINGS_AREAS) {
  const seen = new Set();
  return (areas[title] || [])
    .filter((a) => typeof a === "object" && a.to)
    .filter((a) => (seen.has(a.to) ? false : seen.add(a.to)))
    .map((a) => ({ key: `${title}::${a.to}`, label: a.label, to: a.to }));
}

/**
 * Groups of menu items from the sections this user can see.
 * `blocked(section)` returns a reason the section cannot open (a lock or
 * playground), or null.
 */
export function buildSettingsNav(sections, { blocked = (s) => s.lockedReason || null, areas = SETTINGS_AREAS } = {}) {
  const byTitle = new Map(sections.map((s) => [s.title, s]));
  const item = (section) => {
    const reason = blocked(section);
    return {
      key: section.title,
      title: section.title,
      description: section.description || "",
      icon: SETTINGS_ICONS[section.title] || FALLBACK_ICON,
      to: reason ? null : sectionTarget(section),
      blockedReason: reason,
      children: reason ? [] : sectionChildren(section.title, areas),
    };
  };
  const overview = {
    key: OVERVIEW_TITLE,
    title: OVERVIEW_TITLE,
    description: "",
    icon: SETTINGS_ICONS[OVERVIEW_TITLE],
    to: "/settings",
    blockedReason: null,
    children: [],
  };

  const placed = new Set();
  const groups = SETTINGS_GROUPS.map((g) => ({
    key: g.key,
    label: g.label,
    items: g.titles
      .map((title) => {
        if (title === OVERVIEW_TITLE) return overview;
        const section = byTitle.get(title);
        if (!section) return null;
        placed.add(title);
        return item(section);
      })
      .filter(Boolean),
  }));
  // A section added to Settings.js but not to a group still gets a place.
  const rest = sections.filter((s) => !placed.has(s.title)).map(item);
  if (rest.length) groups.push({ ...OTHER_GROUP, items: rest });
  return groups.filter((g) => g.items.length);
}

function parse(to) {
  const [path, query = ""] = to.split("?");
  return { path, params: new URLSearchParams(query) };
}

/** True when the address `to` names is the current one (its query a subset). */
function sameAddress(to, pathname, search) {
  const { path, params } = parse(to);
  if (path !== pathname) return false;
  const current = new URLSearchParams(search || "");
  for (const [k, v] of params.entries()) if (current.get(k) !== v) return false;
  return true;
}

/**
 * The active item and sub-item for an address. A section's page owns every
 * address below it (/settings/telephony/numbers is Telephony), and Overview
 * is /settings with no section open.
 */
export function activeNavKeys(groups, pathname, search = "") {
  const tab = new URLSearchParams(search).get("tab");
  let best = { item: null, child: null, score: -1 };
  groups.forEach((g) =>
    g.items.forEach((it) => {
      if (!it.to) return;
      let score = -1;
      if (it.title === OVERVIEW_TITLE) {
        if (pathname === "/settings" && !tab) score = 0;
      } else if (sameAddress(it.to, pathname, search)) {
        score = it.to.length + 1;
      } else {
        const { path } = parse(it.to);
        if (path !== "/settings" && pathname.startsWith(`${path}/`)) score = path.length;
      }
      if (score < 0) return;
      const child = it.children.find((c) => sameAddress(c.to, pathname, search)) || null;
      if (child) score += 1000;
      if (score > best.score) best = { item: it.key, child: child ? child.key : null, score };
    })
  );
  return { item: best.item, child: best.child };
}

/**
 * The menu narrowed to a query: sections whose title or description match,
 * or whose inner areas do. A matching area with its own address becomes a
 * sub-item; one without opens its section.
 */
export function filterSettingsNav(groups, query, areas = SETTINGS_AREAS) {
  if (!String(query || "").trim()) return groups;
  const items = groups.flatMap((g) => g.items).filter((it) => it.title !== OVERVIEW_TITLE);
  const results = searchSettings(items, query, areas);
  const hits = new Map(results.map((r) => [r.card.key, r.areas]));
  return groups
    .map((g) => ({
      ...g,
      items: g.items
        .filter((it) => hits.has(it.key))
        .map((it) => {
          const matched = hits.get(it.key);
          if (!matched.length || !it.to) return { ...it, matches: [] };
          const seen = new Set();
          const matches = matched
            .map((a) => ({ key: `${it.key}::match::${a.label}`, label: a.label, to: a.to || it.to }))
            .filter((m) => (seen.has(m.label) ? false : seen.add(m.label)));
          return { ...it, matches };
        }),
    }))
    .filter((g) => g.items.length);
}
